import { useState } from "react";

const OfferDeclarationModal = ({ employee, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    direction: 'received',
    description: '',
    related_entity_name: '',
    related_entity_type: 'client',
    offer_date: new Date().toISOString().split('T')[0],
    estimated_value: '',
    currency: 'EUR',
    context: '',
    business_justification: '',
    notes: ''
  });

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-700">
          <h3 className="text-xl font-bold text-white">Declaração de Oferta</h3>
          <p className="text-sm text-slate-400 mt-1">Declare ofertas recebidas ou oferecidas conforme política de compliance</p>
        </div>
        
        <div className="p-6 space-y-4">
          {/* Direction */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Tipo de Oferta *</label>
            <div className="grid grid-cols-2 gap-4">
              <label className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                formData.direction === 'received' 
                  ? 'bg-blue-900/30 border-blue-600' 
                  : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
              }`}>
                <input
                  type="radio"
                  name="direction"
                  value="received"
                  checked={formData.direction === 'received'}
                  onChange={e => setFormData({...formData, direction: e.target.value})}
                  data-testid="direction-received"
                />
                <div>
                  <p className="font-medium text-white">Oferta Recebida</p>
                  <p className="text-sm text-slate-400">Recebi uma oferta de terceiros</p>
                </div>
              </label>
              
              <label className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                formData.direction === 'offered' 
                  ? 'bg-blue-900/30 border-blue-600' 
                  : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
              }`}>
                <input
                  type="radio"
                  name="direction"
                  value="offered"
                  checked={formData.direction === 'offered'}
                  onChange={e => setFormData({...formData, direction: e.target.value})}
                  data-testid="direction-offered"
                />
                <div>
                  <p className="font-medium text-white">Oferta Oferecida</p>
                  <p className="text-sm text-slate-400">Ofereci algo a terceiros</p>
                </div>
              </label>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Descrição da Oferta *</label>
            <input
              type="text"
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white"
              placeholder="Ex: Jantar de negócios, Bilhetes para evento, Produto promocional"
              data-testid="offer-description"
              required
            />
          </div>

          {/* Related Entity */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                {formData.direction === 'received' ? 'Quem Ofereceu' : 'A Quem Ofereci'} *
              </label>
              <input
                type="text"
                value={formData.related_entity_name}
                onChange={e => setFormData({...formData, related_entity_name: e.target.value})}
                className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white"
                placeholder="Nome da empresa/pessoa"
                data-testid="related-entity-name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Tipo de Entidade</label>
              <select
                value={formData.related_entity_type}
                onChange={e => setFormData({...formData, related_entity_type: e.target.value})}
                className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white"
              >
                <option value="client">Cliente</option>
                <option value="supplier">Fornecedor</option>
                <option value="partner">Parceiro</option>
                <option value="competitor">Concorrente</option>
                <option value="government">Entidade Pública</option>
                <option value="individual">Pessoa Individual</option>
                <option value="other">Outro</option>
              </select>
            </div>
          </div>

          {/* Date and Value */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Data da Oferta *</label>
              <input
                type="date"
                value={formData.offer_date}
                onChange={e => setFormData({...formData, offer_date: e.target.value})}
                className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white"
                data-testid="offer-date"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Valor Estimado</label>
              <input
                type="number"
                step="0.01"
                value={formData.estimated_value}
                onChange={e => setFormData({...formData, estimated_value: e.target.value})}
                className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white"
                placeholder="0.00"
                data-testid="estimated-value"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Moeda</label>
              <select
                value={formData.currency}
                onChange={e => setFormData({...formData, currency: e.target.value})}
                className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white"
              >
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
                <option value="OTHER">Outra</option>
              </select>
            </div>
          </div>

          {/* Context */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Contexto</label>
            <textarea
              value={formData.context}
              onChange={e => setFormData({...formData, context: e.target.value})}
              className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white"
              rows={2}
              placeholder="Em que contexto ocorreu esta oferta?"
            />
          </div>

          {/* Business Justification */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Justificação de Negócio</label>
            <textarea
              value={formData.business_justification}
              onChange={e => setFormData({...formData, business_justification: e.target.value})}
              className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white"
              rows={2}
              placeholder="Qual a relação com atividades de negócio?"
            />
          </div>

          {/* Warning */}
          <div className="p-4 bg-amber-900/30 border border-amber-700 rounded-lg">
            <p className="text-sm text-amber-300">
              Todas as ofertas acima de 50€ são sujeitas a análise do Compliance Officer.
              A não declaração de ofertas pode constituir violação do código de conduta.
            </p>
          </div>
        </div>

        <div className="p-6 border-t border-slate-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-700 text-white hover:bg-slate-600">
            Cancelar
          </button>
          <button
            onClick={() => onSave(formData)}
            disabled={!formData.description || !formData.related_entity_name || !formData.offer_date}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50"
            data-testid="submit-offer-btn"
          >
            Declarar Oferta
          </button>
        </div>
      </div>
    </div>
  );
};

export default OfferDeclarationModal;
